import { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import hotkeys from 'hotkeys-js';
import * as baseDealActionCreators from '@makemydeal/dr-dash-store/actions/baseDealActionCreators';
import * as baseDealSelectors from '@makemydeal/dr-dash-store/selectors/BaseDealSelectors';
import * as interactionAnalyticsActionCreators from '@makemydeal/dr-dash-store/actions/interactionAnalyticsActionCreators';
import { scopedStateSelectors } from '@makemydeal/shared-scoped-store';
import {
    initializeHotkeys,
    isBlockingModalOpen,
    closeOpenSnackbars,
    createHotkeysWithCapsLock
} from '@makemydeal/dr-dash-components';

const { SlideOutType } = baseDealActionCreators;

const keys = ['t', 'f', 'x', 'p', 'i', 'l', 'a'];

const hotkeyToSlideOutMap: Record<string, baseDealActionCreators.SlideOutType> = {
    t: SlideOutType.TradeIn,
    f: SlideOutType.Fees,
    x: SlideOutType.Taxes,
    p: SlideOutType.Protection,
    i: SlideOutType.Incentives,
    l: SlideOutType.Lender,
    a: SlideOutType.Accessories
};

const hotkeyToAnalyticsActionMap: Partial<Record<string, () => { type: string }>> = {
    f: interactionAnalyticsActionCreators.mvFeesLink,
    x: interactionAnalyticsActionCreators.mvTaxesLink,
    p: interactionAnalyticsActionCreators.mvProtectionLink,
    i: interactionAnalyticsActionCreators.mvIncentivesLink,
    l: interactionAnalyticsActionCreators.mvLenderLink,
    a: interactionAnalyticsActionCreators.mvAccessoriesLink
};

/**
 * Hook to register base deal slideout hotkeys for compact UI mode.
 * Handles Alt+T/F/X/P/I/L/A hotkeys with toggle behavior.
 *
 * In multi-scenario mode:
 * - If an input within a specific scenario is focused, the hotkey opens
 *   the slideout for that scenario's data.
 * - If no scenario input is focused, falls back to the primary scenario.
 *
 * For customer-related hotkeys (Buyer, CoBuyer), see:
 * - useBuyerHotkey
 * - useCoBuyerHotkey
 */
export const useSlideoutHotkeys = (enabled = true): void => {
    const dispatch = useDispatch();
    const currentSlideOut = useSelector(baseDealSelectors.getCurrentSlideOut);
    const focusedScopeId = useSelector(scopedStateSelectors.getFocusedScopeId);

    // Use ref to always have the latest value in the hotkey handler
    const currentSlideOutRef = useRef(currentSlideOut);
    const focusedScopeIdRef = useRef(focusedScopeId);

    useEffect(() => {
        currentSlideOutRef.current = currentSlideOut;
    }, [currentSlideOut]);

    useEffect(() => {
        focusedScopeIdRef.current = focusedScopeId;
    }, [focusedScopeId]);

    // Lazily initialize hotkey strings
    const HOTKEYS = useMemo(() => createHotkeysWithCapsLock(keys), []);

    useEffect(() => {
        if (!enabled) {
            return undefined;
        }

        initializeHotkeys();

        hotkeys(HOTKEYS, function (event, handler): void {
            event.preventDefault();

            if (isBlockingModalOpen()) {
                return;
            }

            closeOpenSnackbars();

            const key = handler.key.replace('CapsLock+alt+', '').replace('alt+', '');
            const targetSlideOut = hotkeyToSlideOutMap[key];

            if (targetSlideOut) {
                // Toggle: if same slideout is open, close it; otherwise open it
                if (currentSlideOutRef.current === targetSlideOut) {
                    dispatch(baseDealActionCreators.closeSlideOut());
                } else {
                    // Pass focused scope if available; openSlideOut defaults to primary scope internally
                    const analyticsAction = hotkeyToAnalyticsActionMap[key];
                    if (analyticsAction) dispatch(analyticsAction());
                    dispatch(baseDealActionCreators.openSlideOut(targetSlideOut, focusedScopeIdRef.current));
                }
            }
        });

        return () => {
            hotkeys.unbind(HOTKEYS);
        };
    }, [dispatch, enabled, HOTKEYS]);
};
